import React, {
  useEffect,
  useState,
} from "react";
import {
  EnvelopeSimple,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  mail as mailApi,
} from "@/api";
import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";


export default function MailAddressBookDialog({
  open,
  accountEmail = "",
  onClose,
  onCompose,
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({
    loading: false,
    items: [],
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    if (!open) {
      setSearch("");
      setPage(1);
      setState({
        loading: false,
        items: [],
        total: 0,
        totalPages: 1,
      });
      return undefined;
    }

    let cancelled = false;

    async function load() {
      setState((current) => ({
        ...current,
        loading: true,
      }));

      try {
        const response =
          await mailApi.addressBook({
            search,
            page,
            limit: 50,
          });

        if (cancelled) return;

        setState({
          loading: false,
          items: response?.items || [],
          total: Number(
            response?.total || 0
          ),
          totalPages: Math.max(
            1,
            Number(
              response?.totalPages || 1
            )
          ),
        });
      } catch (error) {
        if (cancelled) return;

        setState((current) => ({
          ...current,
          loading: false,
          items: [],
        }));

        toast.error(
          error?.response?.data?.detail
          || "Impossible de charger le carnet d’adresses"
        );
      }
    }

    const timer = window.setTimeout(
      load,
      search ? 250 : 0
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    open,
    page,
    search,
  ]);

  if (!open) return null;

  return (
    <ActaModal
      open
      onClose={onClose}
      title="Carnet d’adresses"
    >
      <div
        className="min-w-0 space-y-4"
        data-testid="mail-address-book-dialog"
      >
        <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-2">
          <div className="text-xs font-black uppercase tracking-wide text-[var(--do-muted)]">
            Compte associé
          </div>
          <div className="mt-1 break-all text-sm font-bold text-[var(--do-ink)]">
            {accountEmail || "Compte mail connecté"}
          </div>
        </div>

        <label className="flex items-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3">
          <MagnifyingGlass
            size={17}
            className="shrink-0 text-[var(--do-muted)]"
          />
          <Input
            value={search}
            onChange={(event) => {
              setPage(1);
              setSearch(
                event.target.value
              );
            }}
            placeholder="Nom, société ou e-mail"
            className="min-h-[44px] min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
            autoFocus
          />
        </label>

        <div className="text-xs font-bold text-[var(--do-muted)]">
          {state.total} contact{state.total > 1 ? "s" : ""} disponible{state.total > 1 ? "s" : ""}
        </div>

        <div className="max-h-[55vh] overflow-y-auto rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
          {state.loading ? (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Chargement…
            </div>
          ) : state.items.length ? (
            state.items.map((contact) => (
              <div
                key={contact.id}
                className="flex min-w-0 items-center justify-between gap-3 border-b border-[var(--do-border)] p-3 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-black text-[var(--do-ink)]">
                    {contact.name || contact.email}
                  </div>
                  <div className="mt-1 truncate text-xs text-[var(--do-muted)]">
                    {[contact.company, contact.email]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {contact.phone && (
                    <div className="mt-1 truncate text-xs text-[var(--do-muted)]">
                      {contact.phone}
                    </div>
                  )}
                </div>

                <Button
                  type="button"
                  variant="actaPrimary"
                  size="sm"
                  className="shrink-0"
                  onClick={() =>
                    onCompose(contact)
                  }
                  data-testid="mail-address-book-compose"
                >
                  <EnvelopeSimple size={16} />
                  Écrire
                </Button>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-sm text-[var(--do-muted)]">
              Aucun contact disponible dans votre périmètre.
            </div>
          )}
        </div>

        {state.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="actaSecondary"
              size="sm"
              disabled={page <= 1}
              onClick={() =>
                setPage((current) =>
                  Math.max(
                    1,
                    current - 1
                  )
                )
              }
            >
              Précédent
            </Button>

            <span className="text-xs font-bold text-[var(--do-muted)]">
              Page {page} / {state.totalPages}
            </span>

            <Button
              type="button"
              variant="actaSecondary"
              size="sm"
              disabled={
                page
                >= state.totalPages
              }
              onClick={() =>
                setPage((current) =>
                  Math.min(
                    state.totalPages,
                    current + 1
                  )
                )
              }
            >
              Suivant
            </Button>
          </div>
        )}
      </div>
    </ActaModal>
  );
}
